/// <amd-module name="VSS/Features/Charts/Internal/FriendlyNumberFormatter" />
import { format } from "VSS/Core/Util/String";
import * as Resources from "VSS/Features/Charts/Resources";

/**
 * Creates a friendly display for a number to be rendered on a chart
 * This implementation was based on one found in vssui/utilities/friendlynumber but that implementation does not satisfy our needs for precision.
 * This implementation ensures at least 2 digits of precision and a friendly suffix concatenation.
 * This supports up to the Billions in number handling.
 * example: 12000 should be turned into 12K, 2500 should become 2.5k, 120000 should be 120k and so forth.
 * @param value number to be rendered on a chart axis.
 */
export function getFriendlyDisplayValue(value: number): string {
    let displayValue = "";
    let negative = false;

    if (isNaN(value)) {
        return "";
    }

    if (value < 0) {
        negative = true;
        value = -value;
    }

    if (value < 1000) {
        displayValue = value.toString();
    } else if (value < 1000000) {
        const prefix = roundToSignificantDigits(value / 1000);
        displayValue = format(Resources.FriendlyNumberThousandsFormat, prefix.toLocaleString());
    } else if (value < 1000000000) {
        const prefix = roundToSignificantDigits(value / 1000000);
        displayValue = format(Resources.FriendlyNumberMillionsFormat, prefix.toLocaleString());
    } else {
        const prefix = roundToSignificantDigits(value / 1000000000);
        displayValue = format(Resources.FriendlyNumberBillionsFormat, prefix.toLocaleString());
    }

    return (negative ? "-" : "") + displayValue;
}

/**
 * Rounds the given number to either 2 or more significant digits.
 * If the number is a 3 digit or more number then it will be rounded to the number of digits to the right of the decimal point the number is.
 * @param value a number that should be less than 1000
 */
function roundToSignificantDigits(value: number): string {
    // Get the number of digits in the highest period. i.e. 12,000,000 would be "12" and 345,305 would be "345"
    const periodDigits = Math.ceil(Math.log(value) * Math.LOG10E); // Math.log10 isn't supported by IE 11
    // Cut the number to 3 or 2 digits. If we have a number like 5.6834 we want 5.7 if we have 134.555 we want 135
    const precision = Math.max(2, periodDigits);
    return value.toPrecision(precision);
}
